import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Film, ArrowLeft, Users, Calendar, Building2, Trash2, 
  Download, Share2, Heart, MapPin, ExternalLink, Loader2, MoreVertical,
  Copy, MessageCircle, Mail, FileText, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [shortlists, setShortlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [projectRes, shortlistsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers, withCredentials: true }),
        axios.get(`${API}/shortlists?project_id=${projectId}`, { headers, withCredentials: true })
      ]);
      
      setProject(projectRes.data);
      setShortlists(shortlistsRes.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromShortlist = async (shortlistId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/shortlists/${shortlistId}`, { headers, withCredentials: true });
      setShortlists(prev => prev.filter(s => s.shortlist_id !== shortlistId));
    } catch (error) {
      console.error('Error removing from shortlist:', error);
    }
  };

  const handleUpdateStatus = async (shortlistId, status) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/shortlists/${shortlistId}/status?status=${status}`, {}, { headers, withCredentials: true });
      setShortlists(prev => prev.map(s => 
        s.shortlist_id === shortlistId ? { ...s, status } : s
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Helper function to convert Google Drive links to direct image URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    
    if (url.includes('drive.google.com')) {
      let fileId = null;
      
      // Try to extract file ID from various Google Drive URL formats
      const patterns = [
        /\/file\/d\/([a-zA-Z0-9_-]+)/,
        /id=([a-zA-Z0-9_-]+)/,
        /\/d\/([a-zA-Z0-9_-]+)/
      ];
      
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          fileId = match[1];
          break;
        }
      }
      
      if (fileId) {
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w800`;
      }
    }
    
    return url;
  };

  // Share functionality
  const handleShare = async (method) => {
    const projectUrl = `${window.location.origin}/projects/${projectId}`;
    const shareText = `Check out the casting deck for "${project?.name}" on CastDeck - ${shortlists.length} talents shortlisted`;
    
    switch (method) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(projectUrl);
          alert('Link copied to clipboard!');
        } catch (err) {
          const textArea = document.createElement('textarea');
          textArea.value = projectUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Link copied to clipboard!');
        }
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n' + projectUrl)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent('CastDeck - ' + project?.name)}&body=${encodeURIComponent(shareText + '\n\n' + projectUrl)}`, '_blank');
        break;
      default:
        break;
    }
    setShowShareModal(false);
  };

  // PDF Export functionality
  const handleExportPDF = async () => {
    setExporting(true);
    setExportSuccess(false);
    
    try {
      // Create PDF content
      const pdfContent = generatePDFContent();
      
      // Create blob and download
      const blob = new Blob([pdfContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const generatePDFContent = () => {
    const talents = shortlists.map(s => s.talent).filter(Boolean);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${project?.name} - Casting Deck</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            background: #fff; 
            color: #111; 
            padding: 30px;
            font-size: 14px;
          }
          
          /* Header Section */
          .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 4px solid #D4AF37; 
            padding-bottom: 25px;
            background: linear-gradient(to bottom, #faf9f6, #fff);
            padding: 25px;
            margin: -30px -30px 30px -30px;
          }
          .logo { 
            font-size: 36px; 
            font-weight: 800; 
            letter-spacing: 6px; 
            color: #D4AF37;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
          }
          .company-name {
            font-size: 16px;
            color: #666;
            margin-top: 5px;
            font-weight: 500;
          }
          .project-title { 
            font-size: 28px; 
            margin-top: 15px;
            font-weight: 700;
            color: #222;
          }
          .project-meta { 
            color: #555; 
            margin-top: 10px; 
            font-size: 15px;
            font-weight: 500;
          }
          .project-description {
            color: #666;
            margin-top: 10px;
            font-size: 13px;
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
          }
          
          /* Talent Grid */
          .talents-grid { 
            display: grid; 
            grid-template-columns: repeat(2, 1fr); 
            gap: 25px; 
            margin-top: 25px;
          }
          .talent-card { 
            border: 2px solid #e5e5e5; 
            page-break-inside: avoid;
            background: #fff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          
          /* Talent Image - Fixed aspect ratio to prevent face cutting */
          .talent-photos-section {
            background: #f5f5f5;
          }
          .talent-img-container {
            width: 100%;
            height: 250px;
            overflow: hidden;
            background: linear-gradient(135deg, #f5f5f5, #e8e8e8);
            display: flex;
            align-items: flex-start;
            justify-content: center;
          }
          .talent-img { 
            width: 100%; 
            height: 100%;
            object-fit: cover;
            object-position: top center; /* Focus on face/top of image */
          }
          .no-image {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #999;
            font-size: 14px;
          }
          .talent-additional-photos {
            display: flex;
            gap: 4px;
            padding: 4px;
            background: #eee;
          }
          .additional-photo {
            width: 60px;
            height: 60px;
            overflow: hidden;
            background: #ddd;
          }
          .additional-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .more-photos {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
            color: #666;
          }
          
          /* Talent Info */
          .talent-info { 
            padding: 18px;
            background: #fafafa;
          }
          .talent-name { 
            font-size: 20px; 
            font-weight: 700;
            color: #111;
            margin-bottom: 6px;
          }
          .talent-details { 
            color: #444; 
            font-size: 14px; 
            margin-top: 4px;
            line-height: 1.5;
          }
          .talent-contact {
            color: #666;
            font-size: 12px;
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid #eee;
          }
          .talent-tags { 
            margin-top: 10px;
          }
          .tag { 
            display: inline-block; 
            background: #D4AF37; 
            color: #fff;
            padding: 3px 10px; 
            font-size: 11px; 
            margin-right: 5px; 
            margin-bottom: 5px;
            border-radius: 3px;
            font-weight: 500;
          }
          .status { 
            display: inline-block; 
            padding: 5px 14px; 
            font-size: 11px; 
            text-transform: uppercase; 
            font-weight: 700; 
            margin-top: 10px;
            border-radius: 4px;
            letter-spacing: 0.5px;
          }
          .status-shortlisted { background: #dbeafe; color: #1d4ed8; }
          .status-hold { background: #fef3c7; color: #b45309; }
          .status-booked { background: #d1fae5; color: #059669; }
          
          /* Notes */
          .talent-notes {
            margin-top: 10px;
            font-size: 12px;
            color: #666;
            font-style: italic;
            background: #f5f5f5;
            padding: 8px 10px;
            border-radius: 4px;
            border-left: 3px solid #D4AF37;
          }
          
          /* Footer */
          .footer { 
            margin-top: 40px; 
            text-align: center; 
            color: #888; 
            font-size: 12px; 
            border-top: 2px solid #e5e5e5; 
            padding-top: 20px;
          }
          .footer-logo {
            font-size: 18px;
            font-weight: 700;
            color: #D4AF37;
            letter-spacing: 3px;
            margin-bottom: 5px;
          }
          
          /* Print Styles */
          @media print {
            body { padding: 15px; }
            .header { margin: -15px -15px 20px -15px; }
            .talents-grid { grid-template-columns: repeat(2, 1fr); gap: 15px; }
            .talent-img-container { height: 240px; }
            .talent-card { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">CASTDECK</div>
          <div class="company-name">by Bread Jam Productions</div>
          <h1 class="project-title">${project?.name || 'Untitled Project'}</h1>
          <p class="project-meta">
            ${project?.project_type?.replace('_', ' ').toUpperCase() || 'PROJECT'} 
            ${project?.production_house ? ' • ' + project.production_house : ''}
            • ${talents.length} Talent${talents.length !== 1 ? 's' : ''} Shortlisted
          </p>
          ${project?.description ? `<p class="project-description">${project.description}</p>` : ''}
        </div>
        
        <div class="talents-grid">
          ${shortlists.map((shortlist, index) => {
            const talent = shortlist.talent;
            if (!talent) return '';
            const allPhotos = (talent.photos || []).map(p => getImageUrl(p)).filter(Boolean);
            const mainPhoto = allPhotos[0] || defaultImages[index % defaultImages.length];
            const additionalPhotos = allPhotos.slice(1, 4); // Get up to 3 more photos
            
            return `
              <div class="talent-card">
                <div class="talent-photos-section">
                  <div class="talent-img-container">
                    <img src="${mainPhoto}" alt="${talent.full_name}" class="talent-img" onerror="this.parentElement.innerHTML='<div class=no-image>No Photo Available</div>'"/>
                  </div>
                  ${additionalPhotos.length > 0 ? `
                    <div class="talent-additional-photos">
                      ${additionalPhotos.map(photo => `
                        <div class="additional-photo">
                          <img src="${photo}" alt="${talent.full_name}" onerror="this.style.display='none'"/>
                        </div>
                      `).join('')}
                      ${allPhotos.length > 4 ? `
                        <div class="additional-photo more-photos">+${allPhotos.length - 4}</div>
                      ` : ''}
                    </div>
                  ` : ''}
                </div>
                <div class="talent-info">
                  <div class="talent-name">${talent.full_name || 'Unknown'}</div>
                  <div class="talent-details">
                    ${talent.age ? talent.age + ' years' : ''} 
                    ${talent.gender ? ' • ' + talent.gender.charAt(0).toUpperCase() + talent.gender.slice(1) : ''}
                    ${talent.height ? ' • ' + talent.height : ''}
                  </div>
                  <div class="talent-details">
                    ${talent.location ? '📍 ' + talent.location : ''}
                    ${talent.languages?.length ? ' • ' + talent.languages.slice(0, 3).join(', ') : ''}
                  </div>
                  ${talent.phone || talent.email ? `
                    <div class="talent-contact">
                      ${talent.phone && !talent.phone_masked ? '📱 ' + talent.phone : ''}
                      ${talent.email && !talent.email_masked ? (talent.phone ? ' • ' : '') + '✉️ ' + talent.email : ''}
                    </div>
                  ` : ''}
                  ${(talent.look_tags && talent.look_tags.length > 0) ? `
                    <div class="talent-tags">
                      ${talent.look_tags.slice(0, 4).map(tag => `<span class="tag">${tag}</span>`).join('')}
                    </div>
                  ` : ''}
                  <span class="status status-${shortlist.status}">${shortlist.status}</span>
                  ${shortlist.notes ? `<div class="talent-notes">"${shortlist.notes}"</div>` : ''}
                </div>
              </div>
            `;
          }).join('')}
        </div>
        
        <div class="footer">
          <div class="footer-logo">CASTDECK</div>
          <p>Premium Casting Intelligence Platform</p>
          <p style="margin-top:8px;">Generated on ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p style="margin-top:4px; font-size:11px;">www.castdeck.com • Powered by Bread Jam Productions</p>
        </div>
      </body>
      </html>
    `;
  };

  const defaultImages = [
    'https://images.unsplash.com/photo-1762160773119-adb4c011e4d7?w=400&q=80',
    'https://images.unsplash.com/photo-1762160767032-9a639bc9f89e?w=400&q=80',
    'https://images.unsplash.com/photo-1576987212553-c43b8e44bb1f?w=400&q=80'
  ];

  const statusColors = {
    shortlisted: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    hold: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    booked: 'bg-green-500/10 text-green-400 border-green-500/20',
    rejected: 'bg-red-500/10 text-red-400 border-red-500/20'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
          <Button onClick={() => navigate('/projects')} className="btn-gold">Back to Projects</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 nav-glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/projects')} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Projects</span>
            </button>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Film className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl tracking-wide">CASTDECK</span>
            </Link>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-white/10" data-testid="share-btn">
                    <Share2 className="w-4 h-4 mr-2" /> Share Deck
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-border">
                  <DropdownMenuItem onClick={() => handleShare('copy')}>
                    <Copy className="w-4 h-4 mr-2" /> Copy Link
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShare('whatsapp')}>
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleShare('email')}>
                    <Mail className="w-4 h-4 mr-2" /> Email
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                onClick={handleExportPDF} 
                className="btn-gold" 
                disabled={exporting || shortlists.length === 0}
                data-testid="download-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : exportSuccess ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportSuccess ? 'Exported!' : 'Export PDF'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Project Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className={`inline-block px-3 py-1 text-xs font-medium uppercase mb-3 ${
                project.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-muted text-muted-foreground'
              }`}>
                {project.status}
              </span>
              <h1 className="text-4xl font-bold mb-2" data-testid="project-title">{project.name}</h1>
              {project.description && (
                <p className="text-muted-foreground text-lg">{project.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
            <span className="px-3 py-1 bg-muted capitalize">{project.project_type?.replace('_', ' ')}</span>
            {project.production_house && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{project.production_house}</span>
              </div>
            )}
            {project.deadline && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Deadline: {project.deadline}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>{shortlists.length} talents shortlisted</span>
            </div>
          </div>
        </div>

        {/* Shortlisted Talents */}
        <div className="bg-card border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Shortlisted Talents</h2>
            <Button onClick={() => navigate('/talents')} variant="outline" className="border-white/10" data-testid="add-talent-btn">
              Add Talent
            </Button>
          </div>

          {shortlists.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {shortlists.map((shortlist, index) => {
                const talent = shortlist.talent;
                if (!talent) return null;
                
                // Use getImageUrl helper to properly convert Google Drive and other image URLs
                const image = getImageUrl(talent.photos?.[0]) || defaultImages[index % defaultImages.length];
                
                return (
                  <div 
                    key={shortlist.shortlist_id}
                    className="bg-muted/50 border border-border hover:border-primary/30 transition-colors"
                    data-testid={`shortlist-card-${shortlist.shortlist_id}`}
                  >
                    <div className="relative aspect-[3/4]">
                      <img 
                        src={image} 
                        alt={talent.full_name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = defaultImages[index % defaultImages.length]; }}
                      />
                      <span className={`absolute top-2 left-2 px-2 py-1 text-xs font-medium uppercase border ${statusColors[shortlist.status] || 'bg-muted'}`}>
                        {shortlist.status}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 transition-colors" data-testid={`shortlist-menu-${shortlist.shortlist_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border">
                          <DropdownMenuItem onClick={() => navigate(`/talents/${talent.talent_id}`)}>
                            <ExternalLink className="w-4 h-4 mr-2" /> View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUpdateStatus(shortlist.shortlist_id, 'hold')}>
                            Place on Hold
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUpdateStatus(shortlist.shortlist_id, 'booked')}>
                            Confirm Booking
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRemoveFromShortlist(shortlist.shortlist_id)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="p-4">
                      <Link to={`/talents/${talent.talent_id}`}>
                        <h3 className="font-semibold hover:text-primary transition-colors">{talent.full_name}</h3>
                      </Link>
                      <div className="text-sm text-muted-foreground mt-1">
                        {talent.age} yrs • {talent.gender}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3" />
                        <span>{talent.location}</span>
                      </div>
                      {shortlist.notes && (
                        <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border italic">
                          "{shortlist.notes}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state py-16">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-semibold mb-2">No talents shortlisted yet</h3>
              <p className="text-muted-foreground mb-6">Browse the talent directory and add talents to this project</p>
              <Button onClick={() => navigate('/talents')} className="btn-gold" data-testid="browse-talent-empty">
                Browse Talent
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProjectDetail;
